import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { BACKEND_URL } from '../lib/backendUrl';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { WarningIcon, EditIcon, TrashIcon, CloseIcon } from './Icons';
import './SuperAdminDashboard.css';

interface AdminStats {
  total_students: number;
  total_clubs: number;
  total_events: number;
  total_registrations: number;
  total_revenue: number;
  event_analytics?: any[];
}

interface Announcement {
  id: string;
  title: string;
  description: string;
  createdAt: string;
}

interface AnnouncementFormData {
  title: string;
  description: string;
}

interface PromotionRequest {
  id: string;
  title: string;
  description: string;
  category: string;
  collegeName: string;
  image?: string;
  createdBy: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

interface Club {
  id: string;
  name: string;
  description: string;
  created_by: string;
}

interface Event {
  id: string;
  name: string;
  type: string;
  description: string;
  date: string;
  club_id: string;
  clubName?: string;
}

interface Registration {
  id: string;
  userId: string;
  eventId: string;
  eventName?: string;
  clubName?: string;
  userName?: string;
  registeredAt: string;
}

const SuperAdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [formData, setFormData] = useState<AnnouncementFormData>({ title: '', description: '' });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [promotionRequests, setPromotionRequests] = useState<PromotionRequest[]>([]);
  const [loadingPromotions, setLoadingPromotions] = useState(false);
  
  // Detail view states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailType, setDetailType] = useState<'clubs' | 'events' | 'registrations' | null>(null);
  const [detailData, setDetailData] = useState<any[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);

  const API_URL = BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        // Parallelize all initial data fetches
        const [statsRes, announcementsRes, promotionsRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/stats`, { headers }),
          fetch(`${API_URL}/api/announcements`, { headers }),
          fetch(`${API_URL}/api/promotions/pending`, { headers })
        ]);

        if (statsRes.ok) {
          setStats(await statsRes.json());
        }

        if (announcementsRes.ok) {
          setAnnouncements(await announcementsRes.json());
        }

        if (promotionsRes.ok) {
          setPromotionRequests(await promotionsRes.json());
        }
      } catch (error) {
        console.error('Failed to fetch initial dashboard data:', error);
      } finally {
        setLoading(false);
        setLoadingPromotions(false);
      }
    };

    fetchInitialData();
  }, [API_URL, token]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle create/update announcement
  const handleSubmitAnnouncement = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!formData.title.trim() || !formData.description.trim()) {
      setFormError('Title and description are required');
      return;
    }

    setSubmitting(true);

    try {
      const url = editingId 
        ? `${API_URL}/api/announcements/${editingId}`
        : `${API_URL}/api/announcements`;

      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        
        if (editingId) {
          // Update existing announcement
          setAnnouncements(prev =>
            prev.map(ann => ann.id === editingId ? data : ann)
          );
        } else {
          // Add new announcement
          setAnnouncements(prev => [data, ...prev]);
        }

        setFormData({ title: '', description: '' });
        setEditingId(null);
        setShowAnnouncementForm(false);
      } else {
        setFormError('Failed to save announcement');
      }
    } catch (error) {
      console.error('Error saving announcement:', error);
      setFormError('Error saving announcement');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete announcement
  const handleDeleteAnnouncement = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      const response = await fetch(`${API_URL}/api/announcements/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setAnnouncements(prev => prev.filter(ann => ann.id !== id));
      } else {
        alert('Failed to delete announcement');
      }
    } catch (error) {
      console.error('Error deleting announcement:', error);
      alert('Error deleting announcement');
    }
  };

  // Handle edit announcement
  const handleEditAnnouncement = (announcement: Announcement) => {
    setFormData({ title: announcement.title, description: announcement.description });
    setEditingId(announcement.id);
    setShowAnnouncementForm(true);
    setFormError('');
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setFormData({ title: '', description: '' });
    setEditingId(null);
    setShowAnnouncementForm(false);
    setFormError('');
  };

  // Handle approve promotion request
  const handleApprovePromotion = async (id: string) => {
    try {
      const response = await fetch(`${API_URL}/api/promotions/${id}/approve`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setPromotionRequests(prev => prev.filter(req => req.id !== id));
        alert('Promotion approved successfully!');
      } else {
        const error = await response.json();
        const errorMessage = error?.error || 'Unknown error occurred';
        alert(`Failed to approve promotion: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error approving promotion:', error);
      alert(`Error approving promotion: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Handle reject promotion request
  const handleRejectPromotion = async (id: string) => {
    try {
      const response = await fetch(`${API_URL}/api/promotions/${id}/reject`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setPromotionRequests(prev => prev.filter(req => req.id !== id));
        alert('Promotion rejected successfully!');
      } else {
        const error = await response.json();
        const errorMessage = error?.error || 'Unknown error occurred';
        alert(`Failed to reject promotion: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error rejecting promotion:', error);
      alert(`Error rejecting promotion: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Fetch clubs list
  const fetchClubsDetail = async () => {
    setDetailLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/clubs`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setDetailData(data);
        setDetailType('clubs');
        setShowDetailModal(true);
      } else {
        alert('Failed to fetch clubs');
      }
    } catch (error) {
      console.error('Error fetching clubs:', error);
      alert('Error fetching clubs');
    } finally {
      setDetailLoading(false);
    }
  };

  // Fetch events list
  const fetchEventsDetail = async () => {
    setDetailLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/events`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setDetailData(data);
        setDetailType('events');
        setShowDetailModal(true);
      } else {
        alert('Failed to fetch events');
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      alert('Error fetching events');
    } finally {
      setDetailLoading(false);
    }
  };

  // Fetch registrations list
  const fetchRegistrationsDetail = async () => {
    setDetailLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/registrations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setDetailData(data);
        setDetailType('registrations');
        setShowDetailModal(true);
      } else {
        alert('Failed to fetch registrations');
      }
    } catch (error) {
      console.error('Error fetching registrations:', error);
      alert('Error fetching registrations');
    } finally {
      setDetailLoading(false);
    }
  };

  // Close modal
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setDetailType(null);
    setDetailData([]);
  };

  return (
    <div className="super-admin-dashboard">
      {/* Dashboard Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="admin-header"
      >
        <div className="header-top">
          <h1>Super Admin Dashboard</h1>
        </div>
        <p>Welcome, {user?.username}</p>
      </motion.div>

      {loading ? (
        <div className="loading">Loading admin statistics...</div>
      ) : stats ? (
        <div className="admin-stats-grid">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="stat-card"
          >
            <div className="stat-content">
              <h3>Total Students</h3>
              <p className="stat-value">{stats.total_students}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="stat-card clickable"
            onClick={fetchClubsDetail}
          >
            <div className="stat-content">
              <h3>Active Clubs</h3>
              <p className="stat-value">{stats.total_clubs}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="stat-card clickable"
            onClick={fetchEventsDetail}
          >
            <div className="stat-content">
              <h3>Total Events</h3>
              <p className="stat-value">{stats.total_events}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="stat-card clickable"
            onClick={fetchRegistrationsDetail}
          >
            <div className="stat-content">
              <h3>Registrations</h3>
              <p className="stat-value">{stats.total_registrations}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
            className="stat-card revenue"
          >
            <div className="stat-content">
              <h3>Total Revenue</h3>
              <p className="stat-value">₹{stats.total_revenue?.toLocaleString()}</p>
            </div>
          </motion.div>
        </div>
      ) : (
        <div className="error">Failed to load admin statistics</div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="admin-actions"
      >
        <h2>Admin Actions</h2>
        <div className="action-buttons">
          <button className="admin-btn btn-primary">Manage Users</button>
          <button className="admin-btn btn-secondary">Manage Clubs</button>
          <button className="admin-btn btn-secondary">Manage Events</button>
          <button className="admin-btn btn-secondary" onClick={() => setShowAnalyticsModal(true)}>View Analytics</button>
        </div>
      </motion.div>

      {/* Announcements Management Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="announcements-section"
      >
        <div className="announcements-header">
          <h2>Announcements</h2>
          <button
            className="admin-btn btn-primary"
            onClick={() => setShowAnnouncementForm(true)}
          >
            {editingId ? 'Editing...' : '+ New Announcement'}
          </button>
        </div>

        {/* Announcement Form */}
        {showAnnouncementForm && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="announcement-form-container"
          >
            <form onSubmit={handleSubmitAnnouncement} className="announcement-form">
              <h3>{editingId ? 'Edit Announcement' : 'Create New Announcement'}</h3>

              {formError && (
                <div className="form-error">
                  <WarningIcon size={16} color="#ff6b6b" /> {formError}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="title">Title</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter announcement title"
                  disabled={submitting}
                  maxLength={100}
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter announcement description"
                  disabled={submitting}
                  maxLength={500}
                  rows={5}
                />
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="admin-btn btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : editingId ? 'Update Announcement' : 'Create Announcement'}
                </button>
                <button
                  type="button"
                  className="admin-btn btn-secondary"
                  onClick={handleCancelEdit}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Announcements List */}
        <div className="announcements-list">
          {announcements.length === 0 ? (
            <p className="no-announcements">No announcements yet. Create one to get started!</p>
          ) : (
            announcements.map((announcement, index) => (
              <motion.div
                key={announcement.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="announcement-item"
              >
                <div className="announcement-content">
                  <h4 className="announcement-title">{announcement.title}</h4>
                  <p className="announcement-description">{announcement.description}</p>
                  <span className="announcement-date">
                    {new Date(announcement.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="announcement-actions">
                  <button
                    className="action-btn btn-edit"
                    onClick={() => handleEditAnnouncement(announcement)}
                    title="Edit announcement"
                  >
                    <EditIcon size={14} /> Edit
                  </button>
                  <button
                    className="action-btn btn-delete"
                    onClick={() => handleDeleteAnnouncement(announcement.id)}
                    title="Delete announcement"
                  >
                    <TrashIcon size={14} /> Delete
                  </button>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </motion.div>

      {/* Detail Modal */}
      {showDetailModal && (
        <div className="modal-overlay" onClick={closeDetailModal}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="detail-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                {detailType === 'clubs' && 'All Clubs'}
                {detailType === 'events' && 'All Events'}
                {detailType === 'registrations' && 'All Registrations'}
              </h2>
              <button className="modal-close" onClick={closeDetailModal}><CloseIcon size={16} /></button>
            </div>

            <div className="modal-content">
              {detailLoading ? (
                <div className="modal-loading">Loading...</div>
              ) : detailData.length === 0 ? (
                <div className="modal-empty">No {detailType} found</div>
              ) : (
                <div className="detail-list">
                  {detailType === 'clubs' && detailData.map((club: Club) => (
                    <div key={club.id} className="detail-item">
                      <div className="detail-item-title">{club.name}</div>
                      <div className="detail-item-desc">{club.description}</div>
                    </div>
                  ))}
                  
                  {detailType === 'events' && detailData.map((event: Event) => (
                    <div key={event.id} className="detail-item">
                      <div className="detail-item-title">{event.name}</div>
                      <div className="detail-item-meta">
                        <span className="detail-badge">{event.type}</span>
                        <span className="detail-date">{new Date(event.date).toLocaleDateString()}</span>
                      </div>
                      <div className="detail-item-desc">{event.description}</div>
                    </div>
                  ))}
                  
                  {detailType === 'registrations' && detailData.map((reg: Registration, idx: number) => (
                    <div key={reg.id || idx} className="detail-item">
                      <div className="detail-item-title">{reg.eventName || 'Event'}</div>
                      <div className="detail-item-meta">
                        <span className="detail-badge">{reg.clubName || 'Club'}</span>
                        <span className="detail-date">{new Date(reg.registeredAt).toLocaleDateString()}</span>
                      </div>
                      <div className="detail-item-desc">Registration by: {reg.userName || 'User'}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="admin-btn btn-secondary" onClick={closeDetailModal}>Close</button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Analytics Modal */}
      {showAnalyticsModal && (
        <div className="modal-overlay" onClick={() => setShowAnalyticsModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="analytics-modal"
            style={{ 
              background: '#0B1120', 
              border: '1px solid #1f2937', 
              borderRadius: '8px', 
              padding: '24px', 
              width: '80%', 
              maxWidth: '900px', 
              maxHeight: '90vh',
              overflowY: 'auto',
              color: 'white',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, color: '#f8fafc' }}>Platform Analytics</h2>
              <button 
                onClick={() => setShowAnalyticsModal(false)}
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.5rem' }}
              >
                &times;
              </button>
            </div>

            {stats?.event_analytics && stats.event_analytics.length > 0 ? (
              <div className="analytics-content" style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                <div className="chart-container" style={{ height: '350px' }}>
                  <h3 style={{ fontSize: '1.1rem', marginBottom: '16px', color: '#e2e8f0' }}>Event Registrations</h3>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.event_analytics} margin={{ top: 5, right: 30, left: 20, bottom: 25 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                      <XAxis 
                        dataKey="eventName" 
                        stroke="#94a3b8" 
                        angle={-45} 
                        textAnchor="end" 
                        height={60} 
                        tick={{fill: '#94a3b8', fontSize: 12}}
                      />
                      <YAxis stroke="#94a3b8" tick={{fill: '#94a3b8', fontSize: 12}} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '4px', color: '#f8fafc' }}
                        itemStyle={{ color: '#00ffff' }}
                      />
                      <Bar dataKey="registrations" fill="#00ffff" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="table-container" style={{ marginTop: '24px' }}>
                  <h3 style={{ fontSize: '1.1rem', marginBottom: '16px', color: '#e2e8f0' }}>Registration Details</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #334155' }}>
                        <th style={{ padding: '12px 8px', color: '#94a3b8', fontWeight: '500' }}>Event Name</th>
                        <th style={{ padding: '12px 8px', color: '#94a3b8', fontWeight: '500' }}>Event Type</th>
                        <th style={{ padding: '12px 8px', color: '#94a3b8', fontWeight: '500' }}>Registrations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.event_analytics.map((event, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                          <td style={{ padding: '12px 8px', color: '#e2e8f0' }}>{event.eventName}</td>
                          <td style={{ padding: '12px 8px', color: '#cbd5e1', textTransform: 'capitalize' }}>{event.type || 'Standard'}</td>
                          <td style={{ padding: '12px 8px', color: '#00ffff', fontWeight: '600' }}>{event.registrations}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>
                <p>No analytics data available yet.</p>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
